
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, UserSearch, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { usePatients } from '@/hooks/usePatients';

interface PatientSelectorModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  onPatientSelect: (patientId: string) => void;
}

const PatientSelectorModal = ({ open, onClose, title = "Select Patient", onPatientSelect }: PatientSelectorModalProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { patients } = usePatients();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  // Filter patients based on search term
  const filteredPatients = patients.filter(patient => 
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.id.toString().includes(searchTerm)
  );

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedPatientId(null);
      setSearchTerm('');
    }
  }, [open]);

  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
  };

  const handleConfirm = () => {
    if (selectedPatientId) {
      onPatientSelect(selectedPatientId);
      onClose();
    } else {
      toast({
        title: "No patient selected",
        description: "Please select a patient to continue",
        variant: "destructive"
      });
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md md:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Choose a patient to continue
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by name or ID..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <ScrollArea className="max-h-[400px]">
          {filteredPatients.length > 0 ? (
            <div className="space-y-2">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  className={`flex items-center p-3 rounded-md cursor-pointer ${selectedPatientId === patient.id ? 'bg-accent' : 'hover:bg-muted'}`}
                  onClick={() => handlePatientSelect(patient.id)}
                >
                  <Avatar className="h-10 w-10 mr-3">
                    {patient.avatar ? (
                      <AvatarImage src={patient.avatar} alt={patient.name} />
                    ) : (
                      <AvatarFallback>{getInitials(patient.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">{patient.name}</p>
                    <p className="text-sm text-muted-foreground">ID: {patient.id}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{patient.condition}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <UserSearch className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No patients found matching your search</p>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleConfirm}
            disabled={selectedPatientId === null}
          >
            <User className="h-4 w-4 mr-2" />
            Select Patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatientSelectorModal;
